import { Range, Node } from 'domains/reporter/RichTextEditor/core';

import { Flow } from 'flow-to-typescript-codemod';
import type { ToolInteractionPayload } from 'generated/graphql';

import { stringifyRange } from '../../../utils/stringify';

import { logger } from 'modules/logger';
import { marks } from 'domains/reporter/RichTextEditor/analytics/performance';
import { performance } from 'utils/performance';

export type StartTransactionRequest = {
  type: 'start';
  payload: {
    transaction_id: string;
    report_smid?: string;
    report_selection?: {
      anchor: {
        path: number[];
        offset: number;
      };
      focus: {
        path: number[];
        offset: number;
      };
    };
    report_content?: Node[];
    report_fields?: unknown[];
    template_fields?: unknown[];
    focus_mode?: boolean;
    modify_mode?: boolean;
    case_smid?: string;
    template_smid?: string;
    sdk_context?: ToolInteractionPayload;
  };
};

export type CreateStartTransactionRequestOptions = {
  transaction_id: string;
  case_smid?: Nullable<string>;
  template_smid?: Nullable<string>;
  report_smid?: Nullable<string>;
  report_selection?: Range;
  report_content?: Node[];
  report_fields?: unknown[];
  template_fields?: unknown[];
  focus_mode?: boolean;
  modify_mode?: boolean;
  sdk_context?: ToolInteractionPayload;
};

type StartTransactionProps = Flow.Diff<
  CreateStartTransactionRequestOptions,
  {
    transaction_id: string;
    report_selection?: Range;
  }
>;

export type StartTransactionOptions = StartTransactionProps & {
  report_selection?: Range;
  case_smid?: string;
  template_smid?: string;
  focus_mode?: boolean;
  modify_mode?: boolean;
  sdk_context?: ToolInteractionPayload;
};

export const createStartTransactionRequest = ({
  case_smid,
  template_smid,
  transaction_id,
  report_smid,
  report_selection,
  report_content,
  report_fields,
  template_fields,
  focus_mode,
  modify_mode,
  sdk_context,
}: CreateStartTransactionRequestOptions): StartTransactionRequest => {
  return {
    type: 'start',
    payload: {
      transaction_id,
      report_smid: report_smid ?? undefined,
      report_selection:
        report_selection != null
          ? {
              anchor: {
                path: report_selection.anchor.path,
                offset: report_selection.anchor.offset,
              },
              focus: {
                path: report_selection.focus.path,
                offset: report_selection.focus.offset,
              },
            }
          : undefined,
      report_content,
      report_fields,
      template_fields,
      focus_mode,
      modify_mode,
      case_smid: case_smid ?? undefined,
      template_smid: template_smid ?? undefined,
      sdk_context: sdk_context ?? undefined,
    },
  };
};

export type AudioMessage = ArrayBuffer;

export type HypothesisTextResponse = {
  type: 'hypothesis_text';
  payload: {
    transaction_id: string;
    text: string;
    done: boolean;
  };
};

export type StableTextResponse = {
  type: 'stable_text';
  payload: {
    transaction_id: string;
    text: string;
    markers: Array<{
      audioStart: number;
      audioLength: number;
      text: string;
    }>;
    done: boolean;
    substitutedText?: string;
  };
};

export type FocusMapAppendResponse = {
  type: 'focus_map_append';
  payload: {
    text: string;
    section: Array<string>;
  };
};

export type FocusMapDeleteResponse = {
  type: 'focus_map_delete';
  payload: {
    text: string;
    section: Array<string>;
  };
};

export type CommandResponse = {
  type: 'command';
  payload: {
    name: string;
    arguments: {
      string: string;
    };
  };
};

export type SDKCommandResponse = {
  type: 'sdk_command';
};

export type HangStudyCommandResponse = CommandResponse & {
  payload: CommandResponse['payload'] & {
    name: 'hang_study_by_id';
    arguments: {
      smid: string;
    };
  };
};

export type UpdateToolInteractionCommandResponse = CommandResponse & {
  payload: CommandResponse['payload'] & {
    name: 'update_tool_interaction';
    arguments: {
      llm_response_json: string;
    };
  };
};

export type StoppedResponse = {
  type: 'stopped';
  payload: {
    transaction_id: string;
    case_smid: string;
    focus_mode: boolean;
    modify_mode: boolean;
    text: string;
    done: boolean;
  };
};

export type ReportGeneratedResponse = {
  type: 'report_generated';
  payload: {
    transaction_id: string;
  };
};

export type StopTransactionRequest = {
  type: 'stop';
  payload: {
    transaction_id: string;
    focus_mode: boolean;
    modify_mode: boolean;
  };
};

export type CreateStopTransactionRequestOptions = {
  transaction_id: string;
  focus_mode: boolean;
  modify_mode: boolean;
};

export const createStopTransactionRequest = ({
  transaction_id,
  focus_mode,
  modify_mode,
}: CreateStopTransactionRequestOptions): StopTransactionRequest => {
  return {
    type: 'stop',
    payload: {
      transaction_id,
      focus_mode,
      modify_mode,
    },
  };
};

export type ModifyRequest = {
  type: 'modify';
  payload: {
    transaction_id: string;
    audio_offset_time_ms: number;
  };
};
export type ModifyDictationOptions = {
  localSequence?: number;
};

export type GenerateReportRequest = {
  type: 'generate';
  payload: {
    transaction_id: string;
    case_smid: string;
  };
};

export type CreateGenerateReportRequestOptions = {
  transaction_id: string;
  case_smid: string;
};

export const createGenerateReportRequest = ({
  transaction_id,
  case_smid,
}: CreateGenerateReportRequestOptions): GenerateReportRequest => {
  return {
    type: 'generate',
    payload: {
      transaction_id,
      case_smid,
    },
  };
};

export type ErrorResponse = {
  type: 'error';
  payload: {
    transaction_id: string;
    message: string;
  };
};

export type ASRPlexMessage =
  | HypothesisTextResponse
  | StableTextResponse
  | CommandResponse
  | SDKCommandResponse
  | HangStudyCommandResponse
  | UpdateToolInteractionCommandResponse
  | StoppedResponse
  | ReportGeneratedResponse
  | ErrorResponse;

export class ASRPlexTransaction {
  encoder: TextEncoder = new TextEncoder();
  transaction_id: string;
  encoded_transaction_id: Uint8Array;
  state: 'new' | 'started' | 'stopped' = 'new';
  socket: WebSocket;
  selection: Range | null = null;
  selectionKey: string | null = null;
  focus_mode: boolean = false;
  modify_mode: boolean = false;

  constructor(
    transaction_id: string,
    socket: WebSocket,
    focus_mode?: boolean,
    modify_mode?: boolean
  ) {
    this.transaction_id = transaction_id;
    this.encoded_transaction_id = this.encoder.encode(`${transaction_id} `);
    this.focus_mode = focus_mode ?? false;
    this.modify_mode = modify_mode ?? false;
    this.socket = socket;
    this.socket.addEventListener('error', (ev: Event) => {
      logger.error('[asrPlexProtocol] ASRPlexTransaction socket error, stopping transaction.', {
        transaction_id: this.transaction_id,
        event: ev,
      });
      this.state = 'stopped';
    });
  }

  private safeSend(data: string | ArrayBuffer) {
    if (this.socket.readyState !== WebSocket.OPEN) {
      logger.warn('[asrPlexProtocol] Tried to send on non-open socket', {
        readyState: this.socket.readyState,
        transaction_id: this.transaction_id,
      });
      return false;
    }
    this.socket.send(data);
    return true;
  }

  startTransaction({
    case_smid,
    template_smid,
    report_selection,
    report_content,
    report_fields,
    template_fields,
    focus_mode,
    modify_mode,
    sdk_context,
  }: StartTransactionOptions): void {
    if (this.state !== 'new') {
      logger.error(
        `[asrPlexProtocol] startTransaction called when transaction is in state: ${this.state}. Expected state: new`,
        { transaction_id: this.transaction_id }
      );
      return;
    }
    this.selection = report_selection ?? null;
    this.selectionKey = report_selection != null ? stringifyRange(report_selection) : null;
    const startReq = createStartTransactionRequest({
      transaction_id: this.transaction_id,
      case_smid,
      template_smid,
      report_content,
      report_fields,
      report_selection,
      template_fields,
      focus_mode,
      modify_mode,
      sdk_context,
    });
    this.safeSend(JSON.stringify(startReq));
    this.state = 'started';
    performance.mark(marks.nvoq.Dictation.StartDictation, this.transaction_id);
  }

  modifyDictation(audioOffsetMs: number, options?: ModifyDictationOptions): void {
    if (this.state !== 'started') {
      logger.error(
        `[asrPlexProtocol] modifyDictation called when transaction is in state: ${this.state}. Expected state: started`,
        { transaction_id: this.transaction_id }
      );
      return;
    }
    const modifyReq: ModifyRequest = {
      type: 'modify',
      payload: {
        transaction_id: this.transaction_id,
        audio_offset_time_ms: audioOffsetMs,
      },
    };
    const didSend = this.safeSend(JSON.stringify(modifyReq));

    if (didSend) {
      logger.info('[asrPlexProtocol] modify_dictation sent', {
        transactionId: this.transaction_id,
        localSequence: options?.localSequence,
        audioOffsetMs,
      });
    }
  }

  stopTransaction(): void {
    if (this.state !== 'started') {
      logger.error(
        `[asrPlexProtocol] stopTransaction called when transaction is in state: ${this.state}. Expected state: started`,
        { transaction_id: this.transaction_id }
      );
      return;
    }
    const stopReq = createStopTransactionRequest({
      transaction_id: this.transaction_id,
      focus_mode: this.focus_mode,
      modify_mode: this.modify_mode,
    });
    this.safeSend(JSON.stringify(stopReq));
    this.state = 'stopped';
    performance.mark(marks.nvoq.Dictation.DoneMessageSent, this.transaction_id);
  }

  sendAudio(audio: Float32Array): boolean {
    if (this.state !== 'started') {
      logger.error(
        `[asrPlexProtocol] sendAudio called when transaction is in state: ${this.state}. Expected state: started`,
        { transaction_id: this.transaction_id }
      );
      return false;
    }
    const message = new Uint8Array(this.encoded_transaction_id.length + audio.byteLength);
    message.set(this.encoded_transaction_id);
    message.set(new Uint8Array(audio.buffer), this.encoded_transaction_id.length);
    return this.safeSend(message.buffer);
  }
}
